import React from 'react';
import { Checkbox, FormControl, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { Controller } from 'react-hook-form';
import { FormInputProps } from './form-input-props';
import { DataSearchField } from '../data-table/table-search-field/data-search-field';

export interface FormInputMultiDropdownOption {
  label: string;
  value: string;
}

export interface FormInputMultiDropdownProps extends FormInputProps {
  options: FormInputMultiDropdownOption[];
}

export const FormInputMultiDropdown: React.FC<FormInputMultiDropdownProps> = ({ name, control, label, options, variant, testId }) => {
  const [checked, setChecked] = React.useState<number[]>([]);
  const [filterText, setFilterText] = React.useState('');

  // Updates through the setter rather than reading `checked`, so the callback is stable and the memo below can
  // depend on it honestly.
  const handleToggle = React.useCallback(
    (value: number) => () => {
      setChecked((current) => {
        const currentIndex = current.indexOf(value);
        const newChecked = [...current];

        if (currentIndex === -1) {
          newChecked.push(value);
        } else {
          newChecked.splice(currentIndex, 1);
        }

        return newChecked;
      });
    },
    [],
  );

  const filteredOptions = React.useMemo(() => options.filter((option) => option.label.toLowerCase().startsWith(filterText.toLowerCase())), [options, filterText]);

  const getTestId = () => {
    return testId !== undefined ? `select-${testId}` : `select-${name}`;
  };

  const dropdownOptions = React.useMemo(
    () =>
      filteredOptions.map((option: FormInputMultiDropdownOption, index: number) => (
        <ListItemButton role={undefined} onClick={handleToggle(index)} dense key={index}>
          <ListItemIcon>
            <Checkbox edge='start' checked={checked.indexOf(index) !== -1} tabIndex={-1} disableRipple />
          </ListItemIcon>
          <ListItemText id={option.label} primary={option.label} />
        </ListItemButton>
      )),
    // checked and handleToggle are read inside; before filteredOptions was memoised, a bare .filter() gave this memo
    // a new array every render and it recomputed by accident. With the memo above it would have frozen every checkbox.
    [filteredOptions, checked, handleToggle],
  );

  return (
    <FormControl size={'small'} variant={variant != null ? variant : 'outlined'} style={{ marginTop: '0px', marginBottom: '32px', width: '100%' }}>
      <Controller
        name={name}
        control={control}
        render={() => (
          <>
            <DataSearchField onFiltersChange={(filter) => setFilterText(filter)} />
            <List data-test-id={getTestId()} sx={{ maxHeight: 400, overflow: 'auto' }}>
              {dropdownOptions}
            </List>
          </>
        )}
      />
    </FormControl>
  );
};
